import React, { useEffect } from 'react'
import { useIntl } from 'react-intl'
import { useParams, useLocation, Redirect } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useServerContext } from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import { getAppOrigin } from '@salesforce/pwa-kit-react-sdk/utils/url'
import { Carousel, Spinner, JsonLd, formatProduct } from 'msp-components'
import {
  Region,
  transformRegionToMap,
  useEinstein,
  useVariant,
  useNavigation,
  findImageGroupBy,
  configureDISBaseLink,
  productUrlBuilder,
  useCommerceAPI,
  useCookie,
  getEventItemFromProduct,
  useMultiSite,
  useActiveData
} from 'msp-integrations'
import { MAX_CACHE_AGE } from '@/constants'
import Seo from '@/components/seo'
import PageLayout from '@/components/cms/page-layout'
import JasminProductHeader from '@/components/molecules/JasminProductHeader/JasminProductHeader'
import ProductSetDetails from '@/components/molecules/ProductSetDetails/ProductSetDetails'

type ProductDetailProps = {
  isLoading: boolean
  product: any
  page: any
  pageNotFound: boolean
}

const ProductDetail = (props: ProductDetailProps) => {
  const { isLoading, product, page } = props
  const { buildUrl } = useMultiSite()
  const { eventTracking, getCookieAllowedById } = useCookie()
  const { formatMessage } = useIntl()
  const navigate = useNavigation()
  const einstein = useEinstein()
  const activeData = useActiveData()
  const variant = useVariant(product)
  const regions = transformRegionToMap(page?.content.regions)

  const isMarketingConsentAllowed = getCookieAllowedById('c52') // CONSENTS['c52'] = 'marketing'
  const isSalesforceAllowed = getCookieAllowedById('s291') // CONSENTS['s291'] = 'salesforce'

  const handleVariantChange = (newVariantId: string) => {
    if (product?.type?.variant) {
      const pVariant = product.variants?.find(
        (v) => v.productId === newVariantId
      )
      const updatedUrl = productUrlBuilder({
        name: pVariant.name,
        id: pVariant.productId,
        primaryCategoryName: product.c_primaryCategoryName,
        classCategoryName: product.c_classCategoryName,
        primaryCategoryPath: product.c_primaryCategoryPath,
        classCategoryPath: product.c_classCategoryPath
      })
      navigate(updatedUrl)
    }
  }

  useEffect(() => {
    if (product) {
      einstein.sendViewProduct(product)
      activeData.sendViewProduct(
        { id: product.primaryCategoryId },
        product,
        'detail'
      )

      if (isMarketingConsentAllowed) {
        eventTracking('view_item', {
          ecommerce: {
            items: [getEventItemFromProduct(product)]
          }
        })
        localStorage.setItem('lastViewedProduct', product.id)
      }
    }
  }, [product, isMarketingConsentAllowed, isSalesforceAllowed])

  if (isLoading || !product) {
    return (
      <div className='relative col-span-12 h-screen'>
        <Spinner fullScreen />
      </div>
    )
  }

  const largeImageGroup = findImageGroupBy(product.imageGroups, {
    viewType: 'large',
    selectedVariationAttributes: product?.variationValues || {}
  })

  let images = []
  images = largeImageGroup?.images.map((image: any, index: number) => {
    if (image.disBaseLink) {
      image.alt = image.alt || `${product.name} #${index}`
      image.title = image.title || `${product.name} #${index}`
      return {
        ...image,
        disBaseLink: configureDISBaseLink(image.disBaseLink, {
          scaleWidth: 960
        })
      }
    }
    return image
  })

  return (
    <>
      <Seo
        title={
          page.found && page?.meta?.title
            ? page?.meta?.title
            : product?.pageTitle ||
              formatMessage(
                {
                  defaultMessage: `Shop Product {productName}`,
                  id: 'msp.product_detail.seo.title'
                },
                { productName: product?.name || '' }
              )
        }
        description={
          page.found && page?.meta?.description
            ? page?.meta?.description
            : product?.pageDescription ||
              formatMessage(
                {
                  defaultMessage: `You are in the Shop Product {productName}`,
                  id: 'msp.product_detail.seo.description'
                },
                { productName: product?.name || '' }
              )
        }
        keywords={
          page.found && page?.meta?.keywords
            ? page?.meta?.keywords
            : product?.pageKeywords || ''
        }
        canonicals={
          page.found && page?.meta?.canonicals ? page?.meta?.canonicals : []
        }
      />
      {product && (
        <JsonLd
          type='Product'
          object={formatProduct({
            ...product,
            slugUrl: buildUrl(
              productUrlBuilder({
                id: product.productId || product.id,
                name: product.name || product.productName,
                primaryCategoryName: product.c_primaryCategoryName,
                classCategoryName: product.c_classCategoryName,
                primaryCategoryPath: product.c_primaryCategoryPath,
                classCategoryPath: product.c_classCategoryPath
              })
            ),
            image:
              Array.isArray(images) && images.length
                ? images[0].disBaseLink
                : ''
          })}
        />
      )}
      <div className='bg-lightGray flex w-full items-center justify-center'>
        <div className='max-w-screen-3xl flex w-full flex-col bg-white'>
          <div className='flex flex-col md:flex-row'>
            <div className='md:w-1/2'>
              <Carousel
                productImages={images}
                pauseOnHover
                autoplay
                autoplayDuration={3000}
                transitionDuration='transition-700'
                firstImageIsHighPriority
              />
            </div>
            <div className='md:w-1/2'>
              <JasminProductHeader
                product={product}
                variant={variant}
                onVariantChange={handleVariantChange}
              />
            </div>
          </div>
          {product?.type?.set && (
            <ProductSetDetails
              className='mt-20 flex flex-col items-center justify-center md:mt-20 '
              product={product}
              defaultImage={images?.[0]?.disBaseLink}
              bgStyle={'light'}
              imagePosition={'left'}
              contentPosition={'top'}
            />
          )}
          <div className='mt-12'>
            <Region region={regions.get('main')} product={product} />
          </div>
          <Region region={regions.get('bottomRegion')} />
        </div>
      </div>
    </>
  )
}

const ProductDetailWrapper = (props: any) => {
  const { res } = useServerContext()
  const api = useCommerceAPI()
  const location = useLocation()
  const { buildUrl } = useMultiSite()
  const { productPath } = useParams() as {
    productPath: string
  }
  const productId = productPath ? productPath.match(/\d{18}/)[0] : undefined

  const urlParams = new URLSearchParams(location.search)

  if (res) {
    res.set('Cache-Control', `max-age=${MAX_CACHE_AGE}`)
  }

  const { data: product, isLoading } = useQuery(
    ['product', urlParams.get('pid') || productId],
    () =>
      api.shopperProducts.getProduct({
        parameters: {
          id: urlParams.get('pid') || productId,
          allImages: true,
          perPricebook: true
        }
      }),
    {
      refetchOnMount: 'always',
      notifyOnChangeProps: ['data']
    }
  )

  if (typeof product?.type === 'string' || product?.fault) {
    return <Redirect to={buildUrl('/page-not-found')} />
  }

  // If called product url is not the same like the original url, then redirect
  if (product) {
    const path = res
      ? res.locals.originalUrl
      : `${window.location.pathname}${window.location.search}`
    if (path) {
      const parsedUrl = new URL(`${getAppOrigin()}${path}`)
      const originalUrl = buildUrl(
        productUrlBuilder({
          id: product.productId || product.id,
          name: product.name || product.productName,
          primaryCategoryName: product.c_primaryCategoryName,
          classCategoryName: product.c_classCategoryName,
          primaryCategoryPath: product.c_primaryCategoryPath,
          classCategoryPath: product.c_classCategoryPath
        })
      )
      const encParsedUrl = encodeURI(decodeURI(parsedUrl.pathname))
      const encOriginalUrl = encodeURI(decodeURI(originalUrl))
      if (btoa(encParsedUrl) !== btoa(encOriginalUrl)) {
        return <Redirect to={originalUrl} />
      }
    }
  }

  return (
    <PageLayout
      {...Object.assign(
        {
          ChildPage: ProductDetail,
          product
        },
        {
          ...props,
          isLoading
        }
      )}
    />
  )
}

export default ProductDetailWrapper
